// Marqueurs et bulles de la carte — extraits de la page Carte pour être
// réutilisés à l'identique ailleurs (fiche antenne). Les règles d'affichage
// (couleur par qualification, forme selon les types de bornes, priorité,
// contenu de la bulle) ne doivent exister qu'à un seul endroit : dupliquées,
// elles divergeraient à la première évolution.
import { useState } from 'react';
import L from 'leaflet';
import { borneUrl } from './borneUrl';

const TEARDROP_TYPE_FILL = {
  classik: '#0ea5e9',   // sky-500, plus contraste sur fond blanc/carte
  spherik: '#ff0fa4',
  prestige: '#dc2626',
  accessoire: '#1f845a',
};
const TEARDROP_TYPE_TEXT = {
  classik: '#ffffff',
  spherik: '#ffffff',
  prestige: '#ffffff',
  accessoire: '#ffffff',
};
export const TYPE_ORDER = ['classik', 'spherik', 'prestige', 'accessoire'];

// Marqueur "0 borne" — teardrop simple
//   withBorder=true  : bordure blanche (mode 2 par défaut)
//   withBorder=false : aucune bordure (mode 1 — cas 0 borne)
const createSimpleTeardrop = (qualifColor, isHighlighted, isPrioritaire, withBorder = true) => {
  const size = isHighlighted ? 36 : 28;
  const innerSize = isHighlighted ? 12 : 8;
  const borderWidth = isHighlighted ? 4 : 3;
  const starSize = isHighlighted ? 16 : 12;
  const innerHtml = isPrioritaire
    ? `<svg width="${starSize}" height="${starSize}" viewBox="0 0 24 24" fill="#fbbf24" stroke="#7c2d12" stroke-width="1.5" stroke-linejoin="round" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);filter:drop-shadow(0 0 2px rgba(0,0,0,0.4));"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`
    : `<div style="background:white;width:${innerSize}px;height:${innerSize}px;border-radius:50%;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);"></div>`;

  const borderCss = withBorder
    ? `border:${borderWidth}px solid ${isHighlighted ? '#fbbf24' : (isPrioritaire ? '#fde68a' : 'white')};`
    : '';
  // Sans bordure, on indique le highlight/prio via box-shadow doré uniquement.
  const boxShadow = isHighlighted
    ? (withBorder ? '0 4px 20px rgba(251,191,36,0.6)' : '0 0 0 3px #fbbf24, 0 4px 20px rgba(251,191,36,0.6)')
    : (isPrioritaire ? '0 3px 12px rgba(251,191,36,0.45)' : '0 3px 10px rgba(0,0,0,0.3)');

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;background:${qualifColor};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);${borderCss}box-shadow:${boxShadow};">${innerHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// Marqueur "1 type unique" — teardrop avec fond=type, bordure=qualif, count au centre
const createSingleTypeTeardrop = (qualifColor, type, count, isHighlighted, isPrioritaire) => {
  const size = isHighlighted ? 36 : 30;
  const borderWidth = isHighlighted ? 4 : 3;
  const bg = TEARDROP_TYPE_FILL[type] || '#94a3b8';
  const txt = TEARDROP_TYPE_TEXT[type] || '#ffffff';
  const digits = String(count).length;
  const fontSize = isHighlighted
    ? (digits >= 3 ? 11 : digits === 2 ? 13 : 15)
    : (digits >= 3 ? 10 : digits === 2 ? 12 : 14);
  const shadow = 'text-shadow:0 1px 2px rgba(0,0,0,0.35);';

  // Pastille ★ top-right si prio (le centre est pris par le chiffre)
  const starBadge = isPrioritaire
    ? `<span style="position:absolute;top:-3px;right:-3px;width:14px;height:14px;border-radius:50%;background:#fbbf24;border:1.5px solid #fff;transform:rotate(45deg);display:flex;align-items:center;justify-content:center;font-size:9px;line-height:1;color:#7c2d12;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.3);">★</span>`
    : '';

  const innerHtml = `<span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);color:${txt};font-weight:800;font-size:${fontSize}px;line-height:1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;letter-spacing:-0.02em;pointer-events:none;${shadow}">${count}</span>`;

  // Cas 1 type : bordure à la couleur de la qualif (passe en doré au survol)
  const borderColor = isHighlighted ? '#fbbf24' : qualifColor;
  const boxShadow = isHighlighted
    ? '0 4px 20px rgba(251,191,36,0.6)'
    : (isPrioritaire ? '0 3px 12px rgba(251,191,36,0.45)' : '0 3px 10px rgba(0,0,0,0.3)');

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;background:${bg};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:${borderWidth}px solid ${borderColor};box-shadow:${boxShadow};">${innerHtml}${starBadge}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// Marqueur "MODE 2" — version épurée
//   Teardrop classique : fond = qualif, bordure blanche, ⭐ centrale si prio
//   Au-dessus : petits points colorés (1 par type de borne), sans nombre
// Palette mode 2 : classik en bleu vif pour ne pas se fondre dans le fond carte
const MODE2_TYPE_FILL = {
  classik: '#0284c7',   // bleu vif (sky-600) — bien visible sur fond carte
  spherik: '#ff0fa4',
  prestige: '#dc2626',
  accessoire: '#1f845a',
};
const createMode2Icon = (qualifColor, typesCounts, isHighlighted, isPrioritaire) => {
  const size = isHighlighted ? 36 : 28;
  const innerSize = isHighlighted ? 12 : 8;
  const borderWidth = isHighlighted ? 4 : 3;
  const starSize = isHighlighted ? 16 : 12;

  // Points par type au-dessus du marqueur (plus grands pour visibilité)
  const dotSize = isHighlighted ? 14 : 12;
  const dotGap = 4;
  const dotsGapToPin = 4; // espace entre rangée de points et le teardrop

  const ordered = TYPE_ORDER.filter((t) => (typesCounts[t] || 0) > 0);
  const dotsHtml = ordered.map((t) => {
    const bg = MODE2_TYPE_FILL[t] || '#94a3b8';
    return `<span style="display:inline-block;width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${bg};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.28);"></span>`;
  }).join('');

  const dotsRowW = ordered.length * dotSize + Math.max(0, ordered.length - 1) * dotGap;
  const dotsRowH = ordered.length > 0 ? dotSize + 4 : 0;  // +4 pour les bordures blanches (2px chaque côté)

  const innerHtml = isPrioritaire
    ? `<svg width="${starSize}" height="${starSize}" viewBox="0 0 24 24" fill="#fbbf24" stroke="#7c2d12" stroke-width="1.5" stroke-linejoin="round" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);filter:drop-shadow(0 0 2px rgba(0,0,0,0.4));"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`
    : `<div style="background:white;width:${innerSize}px;height:${innerSize}px;border-radius:50%;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);"></div>`;

  const pinBorder = isHighlighted ? '#fbbf24' : (isPrioritaire ? '#fde68a' : 'white');
  const pinShadow = isHighlighted
    ? '0 4px 20px rgba(251,191,36,0.6)'
    : (isPrioritaire ? '0 3px 12px rgba(251,191,36,0.45)' : '0 3px 10px rgba(0,0,0,0.3)');

  // Si pas de bornes : juste le teardrop, pas de rangée de points
  const totalW = Math.max(dotsRowW, size);
  const totalH = (ordered.length > 0 ? dotsRowH + dotsGapToPin : 0) + size;
  const pinLeft = (totalW - size) / 2;
  const dotsLeft = (totalW - dotsRowW) / 2;

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;width:${totalW}px;height:${totalH}px;">
      ${ordered.length > 0 ? `<div style="position:absolute;top:0;left:${dotsLeft}px;display:flex;gap:${dotGap}px;align-items:center;">${dotsHtml}</div>` : ''}
      <div style="position:absolute;bottom:0;left:${pinLeft}px;background:${qualifColor};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);transform-origin:50% 50%;border:${borderWidth}px solid ${pinBorder};box-shadow:${pinShadow};">${innerHtml}</div>
    </div>`,
    iconSize: [totalW, totalH],
    iconAnchor: [totalW / 2, totalH],
    popupAnchor: [0, -totalH],
  });
};

// Marqueur "N types (≥2)" — teardrop couleur qualif avec le nombre TOTAL de
// bornes au centre (plus de carte blanche par type au-dessus du marqueur).
const createTotalCountTeardrop = (qualifColor, total, isHighlighted, isPrioritaire) => {
  const size = isHighlighted ? 36 : 30;
  const digits = String(total).length;
  const fontSize = isHighlighted
    ? (digits >= 3 ? 11 : digits === 2 ? 13 : 15)
    : (digits >= 3 ? 10 : digits === 2 ? 12 : 14);
  const shadow = 'text-shadow:0 1px 2px rgba(0,0,0,0.35);';

  // Pastille ★ top-right si prio (le centre est pris par le chiffre)
  const starBadge = isPrioritaire
    ? `<span style="position:absolute;top:-3px;right:-3px;width:14px;height:14px;border-radius:50%;background:#fbbf24;border:1.5px solid #fff;transform:rotate(45deg);display:flex;align-items:center;justify-content:center;font-size:9px;line-height:1;color:#7c2d12;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.3);">★</span>`
    : '';

  const innerHtml = `<span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);color:#fff;font-weight:800;font-size:${fontSize}px;line-height:1;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;letter-spacing:-0.02em;pointer-events:none;${shadow}">${total}</span>`;

  // Pas de bordure (mode 1 — contact avec bornes) : surbrillance et état
  // prioritaire indiqués uniquement via box-shadow.
  const boxShadow = isHighlighted
    ? '0 0 0 3px #fbbf24, 0 4px 20px rgba(251,191,36,0.6)'
    : (isPrioritaire ? '0 3px 12px rgba(251,191,36,0.45)' : '0 3px 10px rgba(0,0,0,0.3)');

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;background:${qualifColor};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:${boxShadow};">${innerHtml}${starBadge}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// Cache : signature unique par combinaison mode × qualif × types × hl × prio
const _iconCache = new Map();
// Marqueur "MODE 3" — couleurs des types de bornes REMPLISSENT le marqueur
// à parts égales (conic-gradient), bordure fine à la couleur de la qualif,
// ⭐ centrale si prioritaire.
//   0 borne   : marqueur uni couleur qualif, SANS bordure
//   1 type    : fond solide couleur du type + bordure fine qualif
//   N types   : conic-gradient N parts égales + bordure fine qualif
const createMode3Icon = (qualifColor, typesCounts, isHighlighted, isPrioritaire) => {
  const size = isHighlighted ? 40 : 32;
  const innerSize = isHighlighted ? 12 : 9;
  const starSize = isHighlighted ? 18 : 14;

  const ordered = TYPE_ORDER.filter((t) => (typesCounts[t] || 0) > 0);
  const hasBornes = ordered.length > 0;

  // Bordure : fine quand il y a des bornes, AUCUNE bordure sans borne
  const borderWidth = hasBornes ? (isHighlighted ? 3 : 2) : 0;
  const borderCss = hasBornes ? `${borderWidth}px solid ${qualifColor}` : 'none';

  // Construction du fond :
  //   - 0 borne        : couleur qualif unie
  //   - 1 type unique  : couleur solide du type
  //   - N types        : conic-gradient parts égales
  let background;
  if (!hasBornes) {
    background = qualifColor;
  } else if (ordered.length === 1) {
    background = TEARDROP_TYPE_FILL[ordered[0]] || '#94a3b8';
  } else {
    const step = 360 / ordered.length;
    const stops = ordered.map((t, i) => {
      const c = TEARDROP_TYPE_FILL[t] || '#94a3b8';
      const start = (i * step).toFixed(2);
      const end = ((i + 1) * step).toFixed(2);
      return `${c} ${start}deg ${end}deg`;
    }).join(', ');
    background = `conic-gradient(${stops})`;
  }

  const innerHtml = isPrioritaire
    ? `<svg width="${starSize}" height="${starSize}" viewBox="0 0 24 24" fill="#fbbf24" stroke="#7c2d12" stroke-width="1.5" stroke-linejoin="round" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(45deg);filter:drop-shadow(0 0 2px rgba(0,0,0,0.45));"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`
    : `<div style="background:white;width:${innerSize}px;height:${innerSize}px;border-radius:50%;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);box-shadow:0 0 0 2px rgba(0,0,0,0.08);"></div>`;

  const shadow = isHighlighted
    ? `0 4px 20px ${qualifColor}66`
    : '0 3px 10px rgba(0,0,0,0.3)';

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;background:${background};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:${borderCss};box-shadow:${shadow};">${innerHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

/**
 * Repere du lieu RECHERCHE — a ne pas confondre avec une antenne.
 *
 * Tous les marqueurs de la carte sont des gouttes coloriees par qualification.
 * Celui-ci doit se distinguer au premier coup d'oeil, sinon on croit avoir
 * trouve un contact la ou il n'y a qu'une adresse : d'ou l'ardoise sombre, la
 * cible au centre, et le halo qui bat doucement.
 *
 * L'ancre est la POINTE (comme les autres marqueurs) : c'est elle qui designe
 * la coordonnee, pas le centre du dessin.
 */
export function createRepereRechercheIcon() {
  const size = 38;
  return L.divIcon({
    className: 'repere-recherche',
    html: `
      <span class="repere-halo"></span>
      <span class="repere-goutte">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff"
             stroke-width="2.2" stroke-linecap="round">
          <circle cx="12" cy="12" r="7" />
          <line x1="12" y1="1.5" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22.5" />
          <line x1="1.5" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22.5" y2="12" />
        </svg>
      </span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}

export function getCachedIcon(mode, qualifColor, typesCounts, isHighlighted, isPrioritaire) {
  const ordered = TYPE_ORDER.filter((t) => (typesCounts[t] || 0) > 0);
  const totalBornes = ordered.reduce((s, t) => s + typesCounts[t], 0);

  // MODE 3 : couleurs des types remplissent le marqueur, contour qualif fin
  // (sans bordure quand il n'y a pas de borne)
  if (mode === 3) {
    const sig = ordered.join(',');
    const key = `m3|${qualifColor}|${sig}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
    let icon = _iconCache.get(key);
    if (!icon) { icon = createMode3Icon(qualifColor, typesCounts, isHighlighted, isPrioritaire); _iconCache.set(key, icon); }
    return icon;
  }

  // MODE 2 : design épuré — teardrop simple + petits points par type au-dessus
  if (mode === 2) {
    // Sans borne : teardrop simple (identique au mode 1 cas 0)
    if (totalBornes === 0) {
      const key = `m2-simple|${qualifColor}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
      let icon = _iconCache.get(key);
      if (!icon) { icon = createSimpleTeardrop(qualifColor, isHighlighted, isPrioritaire); _iconCache.set(key, icon); }
      return icon;
    }
    const sig = TYPE_ORDER.map((t) => `${t}:${typesCounts[t] || 0}`).join(',');
    const key = `m2|${qualifColor}|${sig}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
    let icon = _iconCache.get(key);
    if (!icon) { icon = createMode2Icon(qualifColor, typesCounts, isHighlighted, isPrioritaire); _iconCache.set(key, icon); }
    return icon;
  }

  // MODE 1 (par défaut) — design détaillé avec carte blanche & comptes
  if (totalBornes === 0) {
    // Mode 1, 0 borne : pas de bordure (withBorder=false)
    const key = `m1-simple|${qualifColor}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
    let icon = _iconCache.get(key);
    if (!icon) { icon = createSimpleTeardrop(qualifColor, isHighlighted, isPrioritaire, false); _iconCache.set(key, icon); }
    return icon;
  }
  if (ordered.length === 1) {
    const t = ordered[0];
    const key = `m1-single|${qualifColor}|${t}:${typesCounts[t]}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
    let icon = _iconCache.get(key);
    if (!icon) { icon = createSingleTypeTeardrop(qualifColor, t, typesCounts[t], isHighlighted, isPrioritaire); _iconCache.set(key, icon); }
    return icon;
  }
  // 2+ types : nombre total de bornes au centre du marqueur
  const key = `m1-total|${qualifColor}|${totalBornes}|${isHighlighted ? 1 : 0}|${isPrioritaire ? 1 : 0}`;
  let icon = _iconCache.get(key);
  if (!icon) { icon = createTotalCountTeardrop(qualifColor, totalBornes, isHighlighted, isPrioritaire); _iconCache.set(key, icon); }
  return icon;
}

export const QUALIFICATION_COLORS = {
  'antenne_principale': '#bf2dfb',
  'profil_intéressant': '#6687d2',
  'historique_antenne': '#4d4d4d',
  'antenne_binome': '#f98090',
  'nouveau': '#db2777',
};

export const QUALIFICATION_LABELS = {
  'antenne_principale': 'Antenne actuelle',
  'profil_intéressant': 'Profil intéressant',
  'historique_antenne': 'Ancienne antenne',
  'antenne_binome': 'Binôme',
  'nouveau': 'Nouveau',
};

export const BORNE_TYPE_COLORS = {
  classik: { bg: '#0ea5e9', color: '#ffffff', label: 'Classik' },
  spherik: { bg: '#ff0fa4', color: '#ffffff', label: 'Spherik' },
  prestige: { bg: '#dc2626', color: '#ffffff', label: 'Prestige' },
  accessoire: { bg: '#1f845a', color: '#ffffff', label: 'Accessoire' },
};

// Flat color map for MultiSelect component
export const BORNE_TYPE_COLOR_MAP = {
  classik: '#0ea5e9',
  spherik: '#ff0fa4',
  prestige: '#dc2626',
  accessoire: '#1f845a',
};

export const MAX_POPUP_BORNES = 3;

export function BornesPopup({ bornes }) {
  const [expanded, setExpanded] = useState(false);
  if (!bornes || bornes.length === 0) return null;

  const types = Object.entries(
    bornes.reduce((acc, b) => {
      const type = b.type_borne || 'autre';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {})
  );

  const visibleBornes = expanded ? bornes : bornes.slice(0, MAX_POPUP_BORNES);
  const hasMore = bornes.length > MAX_POPUP_BORNES;

  return (
    <div className="popup-bornes">
      <div className="popup-bornes-types">
        {types.map(([type, count]) => (
          <span key={type} className="popup-borne-type-badge" style={{
            background: BORNE_TYPE_COLORS[type]?.bg || '#f8fafc',
            color: BORNE_TYPE_COLORS[type]?.color || '#475569'
          }}>
            {count} {BORNE_TYPE_COLORS[type]?.label || type}
          </span>
        ))}
      </div>
      <div className="popup-bornes-nums">
        {visibleBornes.map((b, i) => (
          <span key={b.id}>
            {i > 0 && ', '}
            {(() => {
              const href = borneUrl(b);
              const style = { color: BORNE_TYPE_COLORS[b.type_borne]?.bg || '#475569', fontWeight: 600 };
              // Pas d'URL exploitable → numéro affiché sans lien mort.
              return href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...style, textDecoration: 'underline' }}
                  onClick={(e) => e.stopPropagation()}
                  title="Ouvrir la fiche borne"
                >
                  {b.numero_formated}
                </a>
              ) : (
                <span style={style}>{b.numero_formated}</span>
              );
            })()}
          </span>
        ))}
        {hasMore && (
          <span className="bornes-toggle-btn" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? ' voir moins' : `.. voir + (${bornes.length - MAX_POPUP_BORNES})`}
          </span>
        )}
      </div>
    </div>
  );
}

// Plus de précomputation : on génère les icônes à la volée via getCachedIcon
// (cf. createCustomIcon ci-dessus) selon la combinaison qualif × types × prio
// × highlighted. Cela évite l'explosion combinatoire (5 qualifs × 8 combinaisons
// types × 2 highlight × 2 prio = 160 variantes possibles).

// ── Réutilisation hors de la page Carte ────────────────────────────────
// Mêmes règles d'affichage que la carte principale : couleur par qualification,
// forme selon les types de bornes, mise en avant des contacts prioritaires.

export const qualificationKey = (contact) => {
  const q = contact?.qualification;
  if (!q || q === '') return 'nouveau';
  if (q === 'profil_intéressant' || q === 'profil_interessant') return 'profil_intéressant';
  return q;
};

export const typesCountsFromBornes = (bornes) => {
  const counts = { classik: 0, spherik: 0, prestige: 0, accessoire: 0 };
  (bornes || []).forEach((b) => {
    const t = b.type_borne;
    if (t && counts[t] !== undefined) counts[t] += 1;
  });
  return counts;
};

/** Icône d'un contact, identique à celle de la page Carte (mode détaillé). */
export function markerIconFor(contact, bornes, { mode = 1, selected = false } = {}) {
  const couleur = QUALIFICATION_COLORS[qualificationKey(contact)] || QUALIFICATION_COLORS['nouveau'];
  return getCachedIcon(mode, couleur, typesCountsFromBornes(bornes), selected, !!contact?.prioritaire);
}

/** Contenu de bulle d'un contact : mêmes classes CSS que la page Carte. */
export function ContactMapPopup({ contact, bornes = [], lienProfil }) {
  const qualKey = qualificationKey(contact);
  const types = Object.entries(typesCountsFromBornes(bornes)).filter(([, n]) => n > 0);
  return (
    <div className="popup-content">
      <div className="popup-header">
        <div>
          <strong>{`${contact.prenom || ''} ${contact.nom || ''}`.trim() || 'Contact'}</strong>
          <div
            className="popup-qualification"
            style={{ color: QUALIFICATION_COLORS[qualKey] || '#64748b' }}
          >
            {QUALIFICATION_LABELS[qualKey] || qualKey}
          </div>
        </div>
      </div>

      {types.length > 0 && (
        <div className="popup-bornes-simple">
          <div className="popup-bornes-types">
            {types.map(([type, count]) => (
              <span
                key={type}
                className="popup-borne-type-badge"
                style={{
                  background: BORNE_TYPE_COLORS[type]?.bg || '#f8fafc',
                  color: BORNE_TYPE_COLORS[type]?.color || '#475569',
                }}
              >
                {count} {BORNE_TYPE_COLORS[type]?.label || type}
              </span>
            ))}
          </div>
          <div className="popup-bornes-nums">
            <strong>{bornes.length > 1 ? 'Bornes' : 'Borne'} : </strong>
            {bornes.map((b, idx) => {
              const href = borneUrl(b);
              return (
                <span key={b.id || b.numero_formated}>
                  {idx > 0 && ', '}
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Ouvrir la fiche borne"
                      style={{
                        color: BORNE_TYPE_COLOR_MAP[b.type_borne] || '#475569',
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                    >
                      {b.numero_formated}
                    </a>
                  ) : (
                    b.numero_formated
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {(contact.ville || contact.adresse) && (
        <p className="popup-address">
          {contact.adresse && `${contact.adresse}, `}
          {contact.cp && `${contact.cp} `}
          {contact.ville}
        </p>
      )}

      {lienProfil}
    </div>
  );
}
